/**
 * Cohort Generator Utility
 *
 * Generates realistic voter cohorts for a district based on its characteristics.
 * Used by admins to populate districts with voters during world setup.
 *
 * The generator creates demographic groups (cohorts) with:
 * - Age, education, and income distributions based on settlement type
 * - Ideology vectors influenced by demographics and region
 * - Salience weights reflecting which issues matter most to each group
 * - Turnout rates based on demographic factors
 */

import type {
  AgeBracket,
  EducationLevel,
  IncomeLevel,
  IdeologyVector,
  RegionType,
  SettlementType,
} from '@/types';
import { clampVector } from './ideology';

// ============================================================================
// TUNING CONSTANTS
// These values control the demographic distributions and ideology generation.
// Adjust these to balance gameplay or match specific political contexts.
// ============================================================================

/** Random variance applied to ideology values (±) */
const IDEOLOGY_VARIANCE = 10;

/** Random variance applied to population distribution (±%) */
const POPULATION_VARIANCE_PERCENT = 5;

/** Minimum population for a cohort to be created */
const MIN_COHORT_POPULATION = 100;

// ============================================================================
// DEMOGRAPHIC DISTRIBUTION WEIGHTS BY SETTLEMENT TYPE
// Values are relative weights that will be normalized to percentages.
// ============================================================================

/**
 * Age distribution weights by settlement type.
 * Urban areas skew younger, rural areas skew older.
 */
const AGE_DISTRIBUTIONS: Record<SettlementType, Record<AgeBracket, number>> = {
  urban: {
    youth: 25,
    young_adult: 30,
    middle_aged: 25,
    senior: 20,
  },
  suburban: {
    youth: 20,
    young_adult: 25,
    middle_aged: 35,
    senior: 20,
  },
  rural: {
    youth: 15,
    young_adult: 20,
    middle_aged: 30,
    senior: 35,
  },
};

/**
 * Education distribution weights by settlement type.
 * Urban areas have higher education rates.
 */
const EDUCATION_DISTRIBUTIONS: Record<SettlementType, Record<EducationLevel, number>> = {
  urban: {
    low: 20,
    medium: 40,
    high: 40,
  },
  suburban: {
    low: 25,
    medium: 45,
    high: 30,
  },
  rural: {
    low: 40,
    medium: 40,
    high: 20,
  },
};

/**
 * Income distribution weights by settlement type.
 * Suburban areas have higher income on average.
 */
const INCOME_DISTRIBUTIONS: Record<SettlementType, Record<IncomeLevel, number>> = {
  urban: {
    low: 35,
    middle: 40,
    high: 25,
  },
  suburban: {
    low: 20,
    middle: 45,
    high: 35,
  },
  rural: {
    low: 40,
    middle: 45,
    high: 15,
  },
};

// ============================================================================
// IDEOLOGY MODIFIERS BY DEMOGRAPHIC
// Ideology axis order: [economic_regulation, social_values, civil_liberties,
//                       national_identity, environment, foreign_security,
//                       government_centralization]
// ============================================================================

/**
 * Base ideology modifiers by age bracket.
 * Youth are more progressive, seniors more traditional.
 */
const AGE_IDEOLOGY_MODIFIERS: Record<AgeBracket, IdeologyVector> = {
  youth: [5, 20, 15, 10, 15, 0, 0],
  young_adult: [0, 10, 10, 5, 10, 0, 0],
  middle_aged: [0, 0, 0, 0, 0, 0, 0],
  senior: [-5, -15, -5, -10, -5, 5, 5],
};

/**
 * Ideology modifiers by education level.
 * Higher education correlates with cosmopolitan, progressive, green views.
 */
const EDUCATION_IDEOLOGY_MODIFIERS: Record<EducationLevel, IdeologyVector> = {
  low: [5, -10, -5, -10, -5, 0, 0],
  medium: [0, 0, 0, 0, 0, 0, 0],
  high: [-5, 10, 5, 15, 10, 5, 0],
};

/**
 * Ideology modifiers by income level.
 * High income favors free market, low income favors regulation.
 */
const INCOME_IDEOLOGY_MODIFIERS: Record<IncomeLevel, IdeologyVector> = {
  low: [20, 0, 0, 0, 0, 0, 5],
  middle: [0, 0, 0, 0, 0, 0, 0],
  high: [-20, 0, 5, 5, -5, 5, -5],
};

/**
 * Regional ideology modifiers.
 * Different regions have distinct political cultures.
 */
const REGION_IDEOLOGY_MODIFIERS: Record<RegionType, IdeologyVector> = {
  coastal: [0, 5, 5, 10, 5, 5, 0],
  mountain: [0, -5, 0, -10, 5, -10, -10],
  plains: [-5, 0, 0, 0, -10, 0, 5],
  forest: [5, 5, 0, 0, 15, -5, -5],
  urban_core: [5, 15, 10, 15, 10, 5, 5],
};

// ============================================================================
// SALIENCE WEIGHTS BY DEMOGRAPHIC
// Higher values mean the cohort cares more about that issue when voting.
// ============================================================================

/**
 * Base salience weights by age bracket.
 * Youth care about civil liberties and environment.
 * Seniors care about social values and economic issues.
 */
const AGE_SALIENCE_MODIFIERS: Record<AgeBracket, IdeologyVector> = {
  youth: [0.6, 0.5, 0.8, 0.4, 0.7, 0.3, 0.4],
  young_adult: [0.7, 0.6, 0.7, 0.5, 0.6, 0.4, 0.5],
  middle_aged: [0.8, 0.7, 0.6, 0.6, 0.5, 0.5, 0.6],
  senior: [0.7, 0.8, 0.5, 0.7, 0.4, 0.6, 0.5],
};

/**
 * Salience modifiers by education level.
 * Higher education increases environmental salience.
 */
const EDUCATION_SALIENCE_MODIFIERS: Record<EducationLevel, Partial<Record<number, number>>> = {
  low: { 0: 0.1, 4: -0.1 },
  medium: {},
  high: { 4: 0.15, 3: 0.1 },
};

/**
 * Salience modifiers by income level.
 * Low income cares most about economic issues.
 */
const INCOME_SALIENCE_MODIFIERS: Record<IncomeLevel, Partial<Record<number, number>>> = {
  low: { 0: 0.2 },
  middle: {},
  high: { 0: -0.1, 5: 0.1 },
};

// ============================================================================
// TURNOUT RATES
// ============================================================================

/**
 * Base turnout rates by age bracket.
 * Seniors vote most reliably, youth least.
 */
const AGE_TURNOUT_RATES: Record<AgeBracket, number> = {
  youth: 0.45,
  young_adult: 0.60,
  middle_aged: 0.75,
  senior: 0.80,
};

/**
 * Turnout modifiers by education level.
 */
const EDUCATION_TURNOUT_MODIFIERS: Record<EducationLevel, number> = {
  low: -0.05,
  medium: 0,
  high: 0.10,
};

/**
 * Turnout modifiers by settlement type.
 * Suburban areas have highest turnout.
 */
const SETTLEMENT_TURNOUT_MODIFIERS: Record<SettlementType, number> = {
  urban: 0.02,
  suburban: 0.05,
  rural: -0.03,
};

// ============================================================================
// RELIGION DISTRIBUTION
// ============================================================================

/**
 * Religion distribution by settlement type.
 * Format: [religion, weight]
 */
const RELIGION_DISTRIBUTIONS: Record<SettlementType, [string, number][]> = {
  urban: [
    ['secular', 40],
    ['mainstream', 35],
    ['devout', 15],
    ['minority', 10],
  ],
  suburban: [
    ['secular', 25],
    ['mainstream', 45],
    ['devout', 25],
    ['minority', 5],
  ],
  rural: [
    ['secular', 15],
    ['mainstream', 40],
    ['devout', 40],
    ['minority', 5],
  ],
};

// ============================================================================
// COHORT GENERATION LOGIC
// ============================================================================

/**
 * Demographic combinations to skip (rare/unrealistic).
 * Format: `${age}-${education}-${income}`
 */
const SKIP_COMBINATIONS = new Set([
  'youth-high-high', // Youth rarely have high income
  'youth-low-high', // Youth rarely have high income
  'senior-high-low', // Educated seniors rarely have low income
]);

/**
 * Input parameters for cohort generation.
 */
export interface CohortGeneratorInput {
  districtId: string;
  districtPopulation: number;
  settlementType: SettlementType;
  regionType: RegionType;
}

/**
 * Output format for a generated cohort.
 * Matches the CohortCreate type but with explicit fields.
 */
export interface GeneratedCohort {
  district_id: string;
  age_bracket: AgeBracket;
  education_level: EducationLevel;
  income_level: IncomeLevel;
  religion: string;
  population: number;
  ideology_vector: IdeologyVector;
  salience_weights: IdeologyVector;
  base_turnout: number;
  current_turnout_modifier: number;
}

/**
 * Get the age distribution weights for a settlement type.
 *
 * @param settlementType - The type of settlement
 * @returns Record of age brackets to their relative weights
 */
export function getAgeDistribution(
  settlementType: SettlementType
): Record<AgeBracket, number> {
  return { ...AGE_DISTRIBUTIONS[settlementType] };
}

/**
 * Get the education distribution weights for a settlement type.
 *
 * @param settlementType - The type of settlement
 * @returns Record of education levels to their relative weights
 */
export function getEducationDistribution(
  settlementType: SettlementType
): Record<EducationLevel, number> {
  return { ...EDUCATION_DISTRIBUTIONS[settlementType] };
}

/**
 * Get the income distribution weights for a settlement type.
 *
 * @param settlementType - The type of settlement
 * @returns Record of income levels to their relative weights
 */
export function getIncomeDistribution(
  settlementType: SettlementType
): Record<IncomeLevel, number> {
  return { ...INCOME_DISTRIBUTIONS[settlementType] };
}

/**
 * Calculate the base ideology for a demographic combination.
 * Combines age, education, and income modifiers.
 *
 * @param age - Age bracket
 * @param education - Education level
 * @param income - Income level
 * @returns Base ideology vector before regional modifiers
 */
export function getBaseIdeology(
  age: AgeBracket,
  education: EducationLevel,
  income: IncomeLevel
): IdeologyVector {
  const ageModifiers = AGE_IDEOLOGY_MODIFIERS[age];
  const educationModifiers = EDUCATION_IDEOLOGY_MODIFIERS[education];
  const incomeModifiers = INCOME_IDEOLOGY_MODIFIERS[income];

  const result: IdeologyVector = [0, 0, 0, 0, 0, 0, 0];

  for (let i = 0; i < 7; i++) {
    result[i] = ageModifiers[i] + educationModifiers[i] + incomeModifiers[i];
  }

  return result;
}

/**
 * Apply regional modifiers to an ideology vector.
 *
 * @param ideology - Base ideology vector
 * @param regionType - Type of region
 * @returns Modified ideology vector with regional influences
 */
export function applyRegionalModifier(
  ideology: IdeologyVector,
  regionType: RegionType
): IdeologyVector {
  const regionModifiers = REGION_IDEOLOGY_MODIFIERS[regionType];

  const result: IdeologyVector = [...ideology] as IdeologyVector;

  for (let i = 0; i < 7; i++) {
    result[i] += regionModifiers[i];
  }

  return clampVector(result);
}

/**
 * Calculate salience weights for a demographic combination.
 *
 * @param age - Age bracket
 * @param education - Education level
 * @param income - Income level
 * @returns Salience weights for each ideology axis
 */
export function getSalienceWeights(
  age: AgeBracket,
  education: EducationLevel,
  income: IncomeLevel
): IdeologyVector {
  // Start with age-based salience
  const result: IdeologyVector = [...AGE_SALIENCE_MODIFIERS[age]] as IdeologyVector;

  // Apply education modifiers
  const eduMods = EDUCATION_SALIENCE_MODIFIERS[education];
  for (const [index, modifier] of Object.entries(eduMods)) {
    result[Number(index)] += modifier as number;
  }

  // Apply income modifiers
  const incomeMods = INCOME_SALIENCE_MODIFIERS[income];
  for (const [index, modifier] of Object.entries(incomeMods)) {
    result[Number(index)] += modifier as number;
  }

  // Clamp salience to valid range [0, 1]
  for (let i = 0; i < 7; i++) {
    result[i] = Math.max(0, Math.min(1, result[i]));
  }

  return result;
}

/**
 * Calculate base turnout for a demographic combination.
 *
 * @param age - Age bracket
 * @param education - Education level
 * @param settlementType - Type of settlement
 * @returns Base turnout rate (0 to 1)
 */
export function getBaseTurnout(
  age: AgeBracket,
  education: EducationLevel,
  settlementType: SettlementType
): number {
  const baseTurnout = AGE_TURNOUT_RATES[age];
  const educationModifier = EDUCATION_TURNOUT_MODIFIERS[education];
  const settlementModifier = SETTLEMENT_TURNOUT_MODIFIERS[settlementType];

  const turnout = baseTurnout + educationModifier + settlementModifier;

  // Clamp to valid range
  return Math.max(0.1, Math.min(0.95, turnout));
}

/**
 * Add random variance to an ideology vector.
 *
 * @param ideology - Base ideology vector
 * @param variance - Maximum variance (±)
 * @returns Ideology vector with random variance applied
 */
function applyIdeologyVariance(
  ideology: IdeologyVector,
  variance: number = IDEOLOGY_VARIANCE
): IdeologyVector {
  const result: IdeologyVector = [...ideology] as IdeologyVector;

  for (let i = 0; i < 7; i++) {
    const randomOffset = (Math.random() - 0.5) * 2 * variance;
    result[i] += randomOffset;
  }

  return clampVector(result);
}

/**
 * Apply random variance to a population value.
 *
 * @param population - Base population
 * @param variancePercent - Maximum variance percentage
 * @returns Population with random variance applied
 */
function applyPopulationVariance(
  population: number,
  variancePercent: number = POPULATION_VARIANCE_PERCENT
): number {
  const variance = (Math.random() - 0.5) * 2 * (variancePercent / 100);
  return Math.round(population * (1 + variance));
}

/**
 * Normalize weights to sum to 1.
 *
 * @param weights - Record of keys to weights
 * @returns Record of keys to normalized weights (sum = 1)
 */
function normalizeWeights<T extends string>(
  weights: Record<T, number>
): Record<T, number> {
  const sum = Object.values(weights).reduce((a, b) => (a as number) + (b as number), 0) as number;
  const result = {} as Record<T, number>;

  for (const [key, value] of Object.entries(weights)) {
    result[key as T] = (value as number) / sum;
  }

  return result;
}

/**
 * Select a religion based on settlement type weights.
 *
 * @param settlementType - Type of settlement
 * @returns Selected religion string
 */
function selectReligion(settlementType: SettlementType): string {
  const distribution = RELIGION_DISTRIBUTIONS[settlementType];
  const totalWeight = distribution.reduce((sum, [, weight]) => sum + weight, 0);
  const random = Math.random() * totalWeight;

  let cumulative = 0;
  for (const [religion, weight] of distribution) {
    cumulative += weight;
    if (random <= cumulative) {
      return religion;
    }
  }

  return distribution[0][0]; // Fallback
}

/**
 * Generate all cohorts for a district based on its characteristics.
 * Creates demographic groups with realistic distributions of age, education,
 * income, religion, ideology, and turnout based on settlement and region types.
 *
 * @param input - District characteristics
 * @returns Array of generated cohorts
 *
 * @example
 * const cohorts = generateCohortsForDistrict({
 *   districtId: 'district-123',
 *   districtPopulation: 100000,
 *   settlementType: 'urban',
 *   regionType: 'coastal',
 * });
 */
export function generateCohortsForDistrict(
  input: CohortGeneratorInput
): GeneratedCohort[] {
  const { districtId, districtPopulation, settlementType, regionType } = input;

  const cohorts: GeneratedCohort[] = [];

  // Get normalized distributions
  const ageWeights = normalizeWeights(getAgeDistribution(settlementType));
  const educationWeights = normalizeWeights(getEducationDistribution(settlementType));
  const incomeWeights = normalizeWeights(getIncomeDistribution(settlementType));

  const ageBrackets: AgeBracket[] = ['youth', 'young_adult', 'middle_aged', 'senior'];
  const educationLevels: EducationLevel[] = ['low', 'medium', 'high'];
  const incomeLevels: IncomeLevel[] = ['low', 'middle', 'high'];

  // Track total allocated population for normalization
  let totalAllocated = 0;
  const preCohorts: {
    cohort: Omit<GeneratedCohort, 'population'>;
    rawWeight: number;
  }[] = [];

  // Generate all valid demographic combinations
  for (const age of ageBrackets) {
    for (const education of educationLevels) {
      for (const income of incomeLevels) {
        // Skip unrealistic combinations
        const combinationKey = `${age}-${education}-${income}`;
        if (SKIP_COMBINATIONS.has(combinationKey)) {
          continue;
        }

        // Calculate raw population weight for this combination
        const rawWeight = ageWeights[age] * educationWeights[education] * incomeWeights[income];

        // Calculate ideology
        const baseIdeology = getBaseIdeology(age, education, income);
        const regionalIdeology = applyRegionalModifier(baseIdeology, regionType);
        const finalIdeology = applyIdeologyVariance(regionalIdeology);

        // Calculate salience weights
        const salience = getSalienceWeights(age, education, income);

        // Calculate turnout
        const turnout = getBaseTurnout(age, education, settlementType);

        // Select religion
        const religion = selectReligion(settlementType);

        preCohorts.push({
          cohort: {
            district_id: districtId,
            age_bracket: age,
            education_level: education,
            income_level: income,
            religion,
            ideology_vector: finalIdeology,
            salience_weights: salience,
            base_turnout: turnout,
            current_turnout_modifier: 0,
          },
          rawWeight,
        });

        totalAllocated += rawWeight;
      }
    }
  }

  // Distribute population proportionally and apply variance
  for (const { cohort, rawWeight } of preCohorts) {
    const proportionalPopulation = (rawWeight / totalAllocated) * districtPopulation;
    const finalPopulation = applyPopulationVariance(proportionalPopulation);

    // Only include cohorts with meaningful population
    if (finalPopulation >= MIN_COHORT_POPULATION) {
      cohorts.push({
        ...cohort,
        population: finalPopulation,
      });
    }
  }

  // Adjust populations to match total (accounts for variance and rounding)
  const currentTotal = cohorts.reduce((sum, c) => sum + c.population, 0);
  const adjustment = districtPopulation / currentTotal;

  for (const cohort of cohorts) {
    cohort.population = Math.round(cohort.population * adjustment);
  }

  return cohorts;
}

/**
 * Get a summary of cohort distribution statistics.
 * Useful for admin verification of generated cohorts.
 *
 * @param cohorts - Array of generated cohorts
 * @returns Summary statistics
 */
export function getCohortSummary(cohorts: GeneratedCohort[]): {
  totalPopulation: number;
  cohortCount: number;
  byAge: Record<AgeBracket, number>;
  byEducation: Record<EducationLevel, number>;
  byIncome: Record<IncomeLevel, number>;
  averageTurnout: number;
} {
  const byAge: Record<AgeBracket, number> = {
    youth: 0,
    young_adult: 0,
    middle_aged: 0,
    senior: 0,
  };

  const byEducation: Record<EducationLevel, number> = {
    low: 0,
    medium: 0,
    high: 0,
  };

  const byIncome: Record<IncomeLevel, number> = {
    low: 0,
    middle: 0,
    high: 0,
  };

  let totalPopulation = 0;
  let weightedTurnout = 0;

  for (const cohort of cohorts) {
    totalPopulation += cohort.population;
    byAge[cohort.age_bracket] += cohort.population;
    byEducation[cohort.education_level] += cohort.population;
    byIncome[cohort.income_level] += cohort.population;
    weightedTurnout += cohort.base_turnout * cohort.population;
  }

  return {
    totalPopulation,
    cohortCount: cohorts.length,
    byAge,
    byEducation,
    byIncome,
    averageTurnout: totalPopulation > 0 ? weightedTurnout / totalPopulation : 0,
  };
}
